import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Bold, Italic, Link as LinkIcon, Code, List, Eye, EyeOff, Image as ImageIcon,
  Loader2, AtSign,
} from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import MarkdownContent from "@/components/MarkdownContent";
import AttachmentGrid from "@/components/AttachmentGrid";

/**
 * Fase 14.5 · MarkdownEditor v2
 *
 * Extiende Fase 13.2 con:
 *  - Botón "Subir imagen" → sube a /api/forums/attachments/upload, inserta
 *    ![alt](url) en el textarea Y guarda el attachment_id en un state para
 *    que el padre lo envíe con el thread/post.
 *  - `@` autocomplete → dropdown con top 8 users, click inserta `@username`.
 *
 * Props añadidas vs Fase 13.2:
 *  - attachments: [{id, url, ...}] (controlada por el padre)
 *  - onAttachmentsChange: (nextArray) => void
 *  - mentionProgramSlug: string (para filtrar autocomplete por acceso)
 */
export default function MarkdownEditor({
  value,
  onChange,
  minLength = 0,
  placeholder = "Escribe aquí…",
  rows = 6,
  testid = "md-editor",
  attachments = [],
  onAttachmentsChange,
  mentionProgramSlug = null,
}) {
  const [preview, setPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [mentionState, setMentionState] = useState(null);
  // {open, query, position, top, left, results, activeIdx}
  const ref = useRef(null);
  const fileRef = useRef(null);

  // ----- Toolbar helpers -----
  const wrap = (prefix, suffix = prefix) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || "texto";
    const after = value.slice(end);
    const next = `${before}${prefix}${selected}${suffix}${after}`;
    onChange(next);
    setTimeout(() => {
      if (ref.current) {
        const pos = start + prefix.length;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos + selected.length);
      }
    }, 10);
  };

  const insertLink = () => {
    const url = window.prompt("URL del enlace:");
    if (!url) return;
    wrap("[", `](${url})`);
  };

  const insertList = () => {
    const nl = value.endsWith("\n") || value === "" ? "" : "\n";
    onChange(`${value}${nl}- `);
    setTimeout(() => ref.current?.focus(), 10);
  };

  // ----- Upload de imagen -----
  const triggerUpload = () => {
    if (uploading) return;
    fileRef.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset para poder re-subir el mismo
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen supera 5 MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", file.name.replace(/\.[^.]+$/, ""));
    try {
      const r = await api.post("/forums/attachments/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const att = r.data;
      const url = att.url.startsWith("http") ? att.url : `${BACKEND_URL}${att.url}`;
      const alt = att.alt_text || att.filename || "imagen";
      // Inserta markdown en el body
      const nl = value.endsWith("\n") || value === "" ? "" : "\n";
      onChange(`${value}${nl}![${alt}](${url})\n`);
      // Registra el attachment_id
      onAttachmentsChange?.([...(attachments || []), att]);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (id) => {
    onAttachmentsChange?.((attachments || []).filter((a) => a.id !== id));
  };

  // ----- Mentions autocomplete -----
  const searchMentions = useCallback(async (query) => {
    if (!query || query.length < 1) return [];
    try {
      const params = new URLSearchParams({ q: query });
      if (mentionProgramSlug) params.set("program_slug", mentionProgramSlug);
      const r = await api.get(`/forums/mentions/autocomplete?${params}`);
      return r.data.items || [];
    } catch { return []; }
  }, [mentionProgramSlug]);

  const debouncedSearch = useRef(null);
  const handleTextareaChange = (e) => {
    const nextValue = e.target.value;
    onChange(nextValue);
    // Detectar @ activo
    const pos = e.target.selectionStart;
    const upto = nextValue.slice(0, pos);
    const match = upto.match(/(?:^|[\s(])@([a-zA-Z0-9._\-]{0,64})$/);
    if (match) {
      const q = match[1];
      // programa autocomplete
      if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
      debouncedSearch.current = setTimeout(async () => {
        const results = await searchMentions(q);
        setMentionState({
          open: true, query: q, atPos: pos - q.length - 1,
          results, activeIdx: 0,
        });
      }, 300);
    } else {
      setMentionState(null);
    }
  };

  const insertMention = (username) => {
    if (!mentionState) return;
    const { atPos, query } = mentionState;
    const before = value.slice(0, atPos);
    const after = value.slice(atPos + 1 + query.length);
    const next = `${before}@${username} ${after}`;
    onChange(next);
    setMentionState(null);
    setTimeout(() => {
      if (ref.current) {
        const p = atPos + username.length + 2;
        ref.current.focus();
        ref.current.setSelectionRange(p, p);
      }
    }, 10);
  };

  const onTextareaKeyDown = (e) => {
    if (!mentionState?.open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionState((s) => ({ ...s,
        activeIdx: Math.min((s.results.length || 1) - 1, s.activeIdx + 1) }));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionState((s) => ({ ...s,
        activeIdx: Math.max(0, s.activeIdx - 1) }));
    } else if (e.key === "Enter" || e.key === "Tab") {
      const item = mentionState.results[mentionState.activeIdx];
      if (item) {
        e.preventDefault();
        insertMention(item.username);
      }
    } else if (e.key === "Escape") {
      setMentionState(null);
    }
  };

  useEffect(() => () => {
    if (debouncedSearch.current) clearTimeout(debouncedSearch.current);
  }, []);

  const count = (value || "").length;
  const tooShort = minLength > 0 && count < minLength;

  return (
    <div className="rounded-lg border border-gray-800 bg-[#0F172A]" data-testid={testid}>
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-gray-800 bg-[#111827] rounded-t-lg">
        <div className="flex items-center gap-1">
          <ToolbarBtn onClick={() => wrap("**")} title="Negrita" testid={`${testid}-bold`}><Bold className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => wrap("*")} title="Cursiva" testid={`${testid}-italic`}><Italic className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={insertLink} title="Enlace" testid={`${testid}-link`}><LinkIcon className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => wrap("`")} title="Código" testid={`${testid}-code`}><Code className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={insertList} title="Lista" testid={`${testid}-list`}><List className="w-3.5 h-3.5" /></ToolbarBtn>
          <div className="w-px h-4 bg-gray-800 mx-1" />
          <ToolbarBtn
            onClick={triggerUpload}
            title="Subir imagen (max 5 MB)"
            testid="forum-editor-upload-btn"
            disabled={uploading}
          >
            {uploading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <ImageIcon className="w-3.5 h-3.5" />}
          </ToolbarBtn>
          <div className="text-[10px] text-gray-500 flex items-center gap-0.5">
            <AtSign className="w-3 h-3" /> menciones
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPreview((v) => !v)}
          data-testid="forum-editor-preview-toggle"
          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-gray-700 text-gray-300 hover:bg-white/5"
        >
          {preview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {preview ? "Editar" : "Preview"}
        </button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid="forum-editor-file-input"
        onChange={onFilePicked}
      />
      {preview ? (
        <div className="p-3 min-h-[120px] text-sm text-gray-200">
          {value ? <MarkdownContent>{value}</MarkdownContent> : <p className="text-gray-500 italic">Sin contenido…</p>}
        </div>
      ) : (
        <div className="relative">
          <textarea
            ref={ref}
            value={value}
            onChange={handleTextareaChange}
            onKeyDown={onTextareaKeyDown}
            placeholder={placeholder}
            rows={rows}
            data-testid={`${testid}-textarea`}
            className="w-full bg-transparent px-3 py-2 text-sm text-white font-mono resize-y focus:outline-none"
          />
          {mentionState?.open && mentionState.results.length > 0 && (
            <div
              data-testid="forum-mentions-dropdown"
              className="absolute z-20 top-full left-3 mt-1 w-72 max-h-72 overflow-y-auto rounded-md border border-gray-800 bg-[#111827] shadow-xl"
            >
              {mentionState.results.map((u, i) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => insertMention(u.username)}
                  data-testid={`forum-mention-option-${u.username}`}
                  className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${
                    i === mentionState.activeIdx
                      ? "bg-brand-orange-500/10 border-l-2 border-brand-orange-500"
                      : "hover:bg-white/5 border-l-2 border-transparent"
                  }`}
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url.startsWith("http") ? u.avatar_url : `${BACKEND_URL}${u.avatar_url}`}
                          alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-brand-orange-500/30 flex items-center justify-center text-[10px] font-bold text-brand-orange-500">
                      {(u.full_name || u.username || "?").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white font-mono">@{u.username}</div>
                    <div className="text-[10px] text-gray-400 truncate">{u.full_name}</div>
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-widest text-brand-orange-500 border border-brand-orange-500/40 rounded px-1 py-0.5">
                    {u.role_badge}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Attachments preview */}
      {(attachments || []).length > 0 && (
        <div className="px-3 pb-2 border-t border-gray-800/60">
          <AttachmentGrid
            attachments={attachments}
            editable={true}
            onRemove={removeAttachment}
          />
        </div>
      )}
      <div className="flex items-center justify-between px-2 py-1 border-t border-gray-800 bg-[#0F172A]/60 rounded-b-lg text-[11px]">
        <span className={tooShort ? "text-amber-300" : "text-gray-500"}>
          {tooShort ? `Mínimo ${minLength} caracteres · faltan ${minLength - count}` : "Markdown · imágenes · @menciones"}
        </span>
        <span className="text-gray-500 font-mono">{count}</span>
      </div>
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      disabled={disabled}
      className="p-1.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
